import type {
    ConfigLookup,
    PaletteTokens,
} from '../../configurable-colors-sounds/color-sound-config-resolver';
import type { LabelClassProperties } from '../types';

/**
 * An icon whose baked-in status/priority color can be swapped for a configured one.
 * `accentColors` are the hex values in `svg` that carry that color today.
 */
export interface TintableIcon {
    svg: string;
    accentColors: string[];
}

export interface UniqueValueInfo {
    value: string;
    symbol: { url: string } & Record<string, unknown>;
}

const HEX_RE = /^#?([0-9a-f]{6})$/i;

export function normalizeHex(hex: string | null | undefined): string | undefined {
    const match = hex ? HEX_RE.exec(hex.trim()) : null;
    return match ? `#${match[1].toUpperCase()}` : undefined;
}

/**
 * The hex to tint with, or undefined to keep today's icon. An explicit "no color" also keeps
 * today's icon: the Esri icons have no uncolored variant, and gray already means "GPS offline".
 */
export function getConfiguredHex(lookup: ConfigLookup<PaletteTokens>): string | undefined {
    return lookup.matched && lookup.value ? normalizeHex(lookup.value.hex) : undefined;
}

export function toTintedIconName(baseIconName: string, hex: string): string {
    return `${baseIconName}, ${hex}`;
}

export function recolorSvg(svg: string, accentColors: string[], hex: string): string {
    return accentColors.reduce(
        (result, accent) => result.replace(new RegExp(accent, 'gi'), hex),
        svg
    );
}

/** Replaces each hex color in `replacements`' keys with its value, in one pass, ignoring case. */
export function replaceColors(svg: string, replacements: Record<string, string>): string {
    if (!Object.keys(replacements).length) {
        return svg;
    }
    const byUpperCase = new Map(
        Object.entries(replacements).map(([from, to]) => [from.toUpperCase(), to])
    );
    const pattern = new RegExp(Array.from(byUpperCase.keys()).join('|'), 'gi');
    return svg.replace(pattern, (color) => byUpperCase.get(color.toUpperCase()) ?? color);
}

export function withTextLabelColor(
    label: LabelClassProperties,
    color: string
): LabelClassProperties {
    return label.symbol?.type === 'text' ? { ...label, symbol: { ...label.symbol, color } } : label;
}

export const STALE_ICON_OPACITY = 0.85;

// The GPS pin is the only round-joined path in an offline icon; the map icon asset tests check
// each one. Its fill and outline both become black, whatever the status color. The outline also
// closes the pin's center hole, so the pin reads as solid black.
const STALE_PIN_RE = /<path[^>]*\bstroke-linejoin="round"[^>]*\/>/;
const STALE_PIN_COLOR = '#0A0A0A';

function toBlackPin(pin: string): string {
    return pin
        .replace(/\bfill="[^"]*"/, `fill="${STALE_PIN_COLOR}"`)
        .replace(/\bstroke="[^"]*"/, `stroke="${STALE_PIN_COLOR}"`);
}

/**
 * A stale-GPS icon for a configured status color: the online icon at 85% opacity with the offline
 * icon's GPS pin, drawn solid black at full opacity. Recolor its accents like any online icon.
 */
export function toStaleSvg(onlineSvg: string, offlineSvg: string): string {
    const pin = STALE_PIN_RE.exec(offlineSvg)?.[0];
    return onlineSvg.replace(
        /(<svg[^>]*>)([\s\S]*)(<\/svg>\s*)$/,
        (_match, open: string, body: string, close: string) =>
            `${open}<g opacity="${STALE_ICON_OPACITY}">${body}</g>${
                pin ? toBlackPin(pin) : ''
            }${close}`
    );
}

export function svgToDataUrl(svg: string): string {
    return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/** The configured colors as distinct normalized hexes. */
export function collectConfiguredHexes(colors: PaletteTokens[]): string[] {
    const hexes = new Set<string>();
    colors.forEach(function addPaletteHex(color) {
        const hex = normalizeHex(color.hex);
        if (hex) {
            hexes.add(hex);
        }
    });
    return Array.from(hexes);
}

/**
 * Generates and caches the tinted variants of a set of icons. The cache is bounded by
 * (tintable icons x palette colors), so it never grows with the number of units or events.
 */
export function createTintedIconRegistry(tintableIcons: Record<string, TintableIcon>) {
    const urlByTintedName = new Map<string, string>();

    function getTintedIconName(baseIconName: string, hex: string | undefined): string {
        const icon = tintableIcons[baseIconName];
        if (!hex || !icon) {
            return baseIconName;
        }
        const tintedName = toTintedIconName(baseIconName, hex);
        if (!urlByTintedName.has(tintedName)) {
            urlByTintedName.set(
                tintedName,
                svgToDataUrl(recolorSvg(icon.svg, icon.accentColors, hex))
            );
        }
        return tintedName;
    }

    function getTintedIconUrl(tintedIconName: string): string | undefined {
        return urlByTintedName.get(tintedIconName);
    }

    /** Returns `baseInfos` itself when there's nothing to add, so an unconfigured map renders as today. */
    function withTintedUniqueValueInfos<T extends UniqueValueInfo>(
        baseInfos: T[],
        hexes: string[]
    ): T[] {
        if (!hexes.length) {
            return baseInfos;
        }
        const tintedInfos: T[] = [];
        for (const info of baseInfos) {
            if (!tintableIcons[info.value]) {
                continue;
            }
            for (const hex of hexes) {
                const tintedName = getTintedIconName(info.value, hex);
                tintedInfos.push({
                    ...info,
                    value: tintedName,
                    symbol: { ...info.symbol, url: urlByTintedName.get(tintedName) as string },
                });
            }
        }
        return tintedInfos.length ? [...baseInfos, ...tintedInfos] : baseInfos;
    }

    return { getTintedIconName, getTintedIconUrl, withTintedUniqueValueInfos };
}
